import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import { useGet, usePut } from "../../api";
import {
  IKindredBrandDetail,
  IKindredBrandUpdate,
  IKindredEntityUpdateRequest,
} from "../../interfaces";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Edit2, X, Save } from "react-feather";
import { format } from "date-fns";
import { TagListEditor } from "../../components/workbench";

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  try {
    return format(new Date(dateStr), "yyyy-MM-dd HH:mm");
  } catch {
    return dateStr;
  }
};

export const KindredBrandDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, refetch } = useGet<IKindredBrandDetail>(
    `kindred/brands/${id}`,
    true
  );

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<IKindredBrandUpdate>({});
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [comment, setComment] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const updateBrand = usePut<
    IKindredBrandDetail,
    IKindredEntityUpdateRequest<IKindredBrandUpdate>
  >();

  useEffect(() => {
    if (data && editing) {
      setForm({
        name: data.name,
        countryCode: data.countryCode,
        logo: data.logo,
        topBrandCountries: data.topBrandCountries,
      });
    }
  }, [data, editing]);

  const startEdit = () => {
    setSaveError(null);
    setSaveSuccess(false);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm({});
    setComment("");
    setSaveError(null);
  };

  const handleSave = async () => {
    try {
      setSaveError(null);
      const changed: Partial<IKindredBrandUpdate> = {};
      for (const key of Object.keys(form) as (keyof IKindredBrandUpdate)[]) {
        if (key === "topBrandCountries") {
          if (
            data &&
            JSON.stringify(form.topBrandCountries ?? []) !==
              JSON.stringify(data.topBrandCountries ?? [])
          ) {
            changed.topBrandCountries = form.topBrandCountries;
          }
          continue;
        }
        if (
          data &&
          String(form[key] ?? "") !== String((data as any)[key] ?? "")
        ) {
          (changed as any)[key] = form[key];
        }
      }
      if (Object.keys(changed).length === 0) {
        setSaveError("No changes detected");
        return;
      }
      await updateBrand.execute(`kindred/brands/${id}`, {
        data: changed,
        comment,
      });
      setShowSaveModal(false);
      setEditing(false);
      setComment("");
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
      await refetch();
    } catch (e: any) {
      setSaveError(e?.message || "Failed to save changes");
    }
  };

  const updateField = (field: keyof IKindredBrandUpdate, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" /> Loading brand...
      </div>
    );
  }

  if (error) {
    return <div className="text-danger">Error: {error.message}</div>;
  }

  if (!data) {
    return <div className="text-muted">Brand not found.</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>
          Brand: {data.name}
          {data.isTopBrand && (
            <Badge bg="info" className="ms-2">
              ★ Top Brand
            </Badge>
          )}
        </h4>
        <div className="d-flex gap-2">
          <Link to="/kindred/brands">
            <Button size="sm" variant="outline-secondary">
              ← Back to Brands
            </Button>
          </Link>
          <Link to={`/kindred/brands/${id}/audit`}>
            <Button size="sm" variant="outline-info">
              View Audit Trail
            </Button>
          </Link>
          {!editing ? (
            <Button size="sm" variant="primary" onClick={startEdit}>
              <Edit2 size={14} className="me-1" /> Edit
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={cancelEdit}
              >
                <X size={14} className="me-1" /> Cancel
              </Button>
              <Button
                size="sm"
                variant="success"
                onClick={() => setShowSaveModal(true)}
              >
                <Save size={14} className="me-1" /> Save
              </Button>
            </>
          )}
        </div>
      </div>

      {saveSuccess && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSaveSuccess(false)}
        >
          Brand updated successfully.
        </Alert>
      )}
      {saveError && !showSaveModal && (
        <Alert variant="danger" dismissible onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Row className="mb-3">
        <Col md={6}>
          <Card>
            <Card.Header>Brand Details</Card.Header>
            <Card.Body>
              <Table size="sm" borderless>
                <tbody>
                  <tr>
                    <td className="fw-bold" style={{ width: "40%" }}>
                      ID
                    </td>
                    <td>{data.id}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Name</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          value={form.name ?? ""}
                          onChange={(e) => updateField("name", e.target.value)}
                        />
                      ) : (
                        data.name
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Country</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          value={form.countryCode ?? ""}
                          onChange={(e) =>
                            updateField("countryCode", e.target.value)
                          }
                        />
                      ) : (
                        data.countryCode || "—"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Created</td>
                    <td>{formatDate(data.createdAt)}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Updated</td>
                    <td>{formatDate(data.updatedAt)}</td>
                  </tr>
                  {data.logo && (
                    <tr>
                      <td className="fw-bold">Logo</td>
                      <td>
                        {editing ? (
                          <Form.Control
                            size="sm"
                            value={form.logo ?? ""}
                            onChange={(e) =>
                              updateField("logo", e.target.value)
                            }
                            placeholder="Logo URL"
                          />
                        ) : (
                          <img
                            src={data.logo}
                            alt={data.name}
                            style={{ maxHeight: 60 }}
                          />
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          {editing ? (
            <>
              <TagListEditor
                label="Top Brand Countries"
                items={form.topBrandCountries ?? []}
                onChange={(topBrandCountries) =>
                  setForm((prev) => ({ ...prev, topBrandCountries }))
                }
                placeholder="Add country code (e.g. GB)..."
                lockedItems={data.topBrandCountries}
              />
              <Alert variant="warning" className="mt-2 mb-0 small">
                You can only add countries here. To remove a top brand country,
                contact an administrator.
              </Alert>
            </>
          ) : (
            <Card>
              <Card.Header>
                Top Brand Countries ({data.topBrandCountries.length})
              </Card.Header>
              <Card.Body>
                {data.topBrandCountries.length === 0 ? (
                  <span className="text-muted">Not a top brand</span>
                ) : (
                  data.topBrandCountries.map((c) => (
                    <Badge key={c} bg="info" className="me-1 mb-1">
                      {c}
                    </Badge>
                  ))
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Header>Domains ({data.domains.length})</Card.Header>
        <Card.Body>
          {data.domains.length === 0 ? (
            <span className="text-muted">No domains</span>
          ) : (
            <Table size="sm" striped>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Country</th>
                  <th>Validated</th>
                </tr>
              </thead>
              <tbody>
                {data.domains.map((d, i) => (
                  <tr key={i}>
                    <td>{d.domain}</td>
                    <td>{d.countryCode || "—"}</td>
                    <td>
                      {d.isValidated ? (
                        <Badge bg="success">Yes</Badge>
                      ) : (
                        <Badge bg="secondary">No</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Save Comment Modal */}
      <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Save Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Comment (required)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Describe the reason for this change..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Form.Group>
          {saveError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {saveError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            disabled={!comment.trim() || updateBrand.loading}
            onClick={handleSave}
          >
            {updateBrand.loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
